import { useState, useEffect } from 'react';
import { toast } from '../../../util/toasts';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { Field, FieldCounter } from '../../../components/field/field';
import { Modal } from '../../../components/modal/modal';
import { CreateLineItemRequest, Invoice, LineItem } from '../../../clients/quipli-service/invoice/invoice';
import { Typography } from '../../../components/typography/typography';
import { displayMoneyFormat, safeRound } from '../../../util/util';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Button } from '../../../components/form/button';
import { useAccountingApiV1 } from '../../../clients/quipli-service/accounting/accounting-api-v1';
import { GeneralLedgerAccount, GeneralLedgerAccountType } from '../../../clients/quipli-service/accounting/accounting';
import { TaxCategory } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import _ from 'lodash';
import { TextArea } from '../../../components/textarea/textarea';

export const EditLineItemModal = ({
  lineItem,
  isOpen,
  currency,
  invoice,
  close,
}: {
  lineItem: LineItem;
  isOpen: boolean;
  currency: string;
  invoice: Invoice;
  close: () => void;
}) => {
  const { updateLineItem } = useInvoiceApiV1();
  const [updatedLineItem, setUpdatedLineItem] = useState<CreateLineItemRequest>(lineItem);
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>();
  const { listAccounts } = useAccountingApiV1();
  const { searchTaxCategories } = useTaxApiV1();

  useEffect(() => {
    listAccounts(false, GeneralLedgerAccountType.Income).then(setAccounts);
    invoice.taxCode && searchTaxCategories({}).then(setTaxCategories);
  }, []);

  useEffect(() => {
    setUpdatedLineItem(lineItem);
  }, [lineItem]);

  async function editLineItem() {
    try {
      await updateLineItem(lineItem.invoiceId, lineItem.id, updatedLineItem);
      toast('Line item edited successfully');
      close();
    } catch (e) {
      toast('Error editing line item', 'error');
    }
  }

  return (
    <Modal isOpen={isOpen} title={'Edit Line Item'} close={() => close()}>
      <div className="edit-line-item grid gap-3">
        <div>
          <label>Name</label>
          <Field
            className="mt-0.5"
            value={updatedLineItem.name}
            onChange={(v) => setUpdatedLineItem({ ...updatedLineItem, name: v })}
            type="text"
          />
        </div>
        <div>
          <label>General Ledger Account</label>
          <Field
            className="mt-0.5"
            value={{
              label: accounts?.find((a) => a.id === updatedLineItem.generalLedgerAccountId)?.name,
              value: updatedLineItem.generalLedgerAccountId,
            }}
            onChange={(v) => setUpdatedLineItem({ ...updatedLineItem, generalLedgerAccountId: v.value })}
            dropdownProps={{ menuPortalTarget: document.body }}
            type="dropdown"
            options={accounts?.map((a) => ({ label: a.name, value: a.id }))}
          />
        </div>
        {invoice.taxCode && taxCategories && taxCategories.length > 0 && (
          <div>
            <label>Tax Category</label>
            <Field
              type="dropdown"
              value={
                updatedLineItem.taxCategoryId && {
                  label: taxCategories?.find((a) => a.id === updatedLineItem.taxCategoryId)?.name,
                  value: updatedLineItem.taxCategoryId,
                }
              }
              onChange={(v) => {
                const { taxCategoryId, taxable, ...remaining } = updatedLineItem;
                setUpdatedLineItem({
                  ...remaining,
                  taxCategoryId: v?.value || undefined,
                  taxable: v ? true : taxable,
                });
              }}
              options={taxCategories?.map((a) => ({ label: a.name, value: a.id }))}
              dropdownProps={{ menuPortalTarget: document.body }}
              isClearable={true}
            />
          </div>
        )}
        <div>
          <label>Description</label>
          <TextArea
            className="mt-0.5 text-sm"
            value={updatedLineItem.description}
            onChange={(event) => setUpdatedLineItem({ ...updatedLineItem, description: event.target.value })}
          />
        </div>

        <div className="grid grid-cols-4 gap-y-1 gap-x-3 items-center">
          <label className="col-span-2">Unit Price</label>
          <label>Qty.</label>
          <label className="flex justify-end">Total</label>

          <Field
            className="col-span-2"
            value={updatedLineItem.unitPrice}
            onChange={(v) => setUpdatedLineItem({ ...updatedLineItem, unitPrice: v })}
            type="currency"
          />
          <FieldCounter
            min={1}
            allowDecimals={false}
            onChange={(v) => setUpdatedLineItem({ ...updatedLineItem, quantity: v })}
            value={updatedLineItem.quantity}
          />
          <div className="flex justify-end">
            <Typography type="text-md">
              {displayMoneyFormat(updatedLineItem.unitPrice * updatedLineItem.quantity, currency)}
            </Typography>
          </div>
        </div>
        <div className="flex justify-between mb-2">
          <Checkbox
            className="mt-2"
            label="Taxable"
            isChecked={updatedLineItem.taxable}
            onChange={(v) => setUpdatedLineItem({ ...updatedLineItem, taxable: v })}
            isDisabled={!!updatedLineItem.taxCategoryId}
          />
          <LineItemTax lineItem={updatedLineItem} invoice={invoice} currency={currency} />
        </div>
        <div className="flex justify-end gap-2">
          <Button className="button-secondary" onClick={() => close()}>
            Cancel
          </Button>
          <Button className="button-primary" onClick={() => editLineItem()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const LineItemTax = ({
  lineItem,
  invoice,
  currency,
}: {
  lineItem: CreateLineItemRequest;
  invoice: Invoice;
  currency: string;
}) => {
  if (!lineItem.taxable) {
    return null;
  } else {
    const taxAmount = lineItemTaxCalculation(lineItem, invoice);
    return <span className="text-[var(--grey-500)] text-sm">Tax: {displayMoneyFormat(taxAmount, currency)}</span>;
  }
};

export function lineItemTaxCalculation(lineItem: CreateLineItemRequest, invoice: Invoice): number {
  if (!lineItem.taxable) {
    return 0;
  }
  let taxRate;
  if (invoice.taxCode) {
    taxRate =
      _.sum(invoice.taxCode.jurisdictions.filter((j) => j.taxCategories.length === 0).map((j) => j.percentage)) / 100;
    if (lineItem.taxCategoryId) {
      taxRate +=
        (invoice.taxCode.jurisdictions.find((j) => j.taxCategories.find((tc) => tc.id === lineItem.taxCategoryId))
          ?.percentage || 0) / 100;
    }
  } else {
    taxRate = invoice.taxRate;
  }
  return safeRound(lineItem.unitPrice * lineItem.quantity * taxRate * 100) / 100;
}
